/**
 * Dungeon Resources
 *
 * Special resources found only in dungeons, separate from kingdom resources.
 * Used for crafting dungeon-specific items, upgrading equipment, and progression.
 */

export const DUNGEON_RESOURCES = {
  // Common dungeon materials
  bone: {
    id: 'bone',
    name: 'Bone',
    icon: '🦴',
    rarity: 'common',
    description: 'Remains of defeated enemies. Used for basic crafting.',
    stackSize: 999,
  },

  leather: {
    id: 'leather',
    name: 'Leather',
    icon: '🧳',
    rarity: 'common',
    description: 'Tough hide from dungeon creatures. Used for armor crafting.',
    stackSize: 999,
  },

  cloth: {
    id: 'cloth',
    name: 'Cloth',
    icon: '🧵',
    rarity: 'common',
    description: 'Tattered fabric from dungeon dwellers. Used for light armor.',
    stackSize: 999,
  },

  ironOre: {
    id: 'ironOre',
    name: 'Iron Ore',
    icon: '⛏️',
    rarity: 'common',
    description: 'Raw iron found in dungeon walls. Can be smelted into weapons.',
    stackSize: 999,
  },

  leatherScraps: {
    id: 'leatherScraps',
    name: 'Leather Scraps',
    icon: '🧳',
    rarity: 'common',
    description: 'Small pieces of leather. Can be combined for crafting.',
    stackSize: 999,
  },

  wolfFang: {
    id: 'wolfFang',
    name: 'Wolf Fang',
    icon: '🦷',
    rarity: 'common',
    description: 'Sharp fang from a dungeon wolf. Used for weapons.',
    stackSize: 999,
  },

  fur: {
    id: 'fur',
    name: 'Fur',
    icon: '🧶',
    rarity: 'common',
    description: 'Thick fur from dungeon beasts. Used for warm armor.',
    stackSize: 999,
  },

  rustyScrap: {
    id: 'rustyScrap',
    name: 'Rusty Scrap',
    icon: '🔩',
    rarity: 'common',
    description: 'Old metal fragments. Can be melted down.',
    stackSize: 999,
  },

  wood: {
    id: 'wood',
    name: 'Wood',
    icon: '🌲',
    rarity: 'common',
    description: 'Wooden debris from dungeon structures. Used for basic crafting.',
    stackSize: 999,
  },

  // Uncommon materials
  essence: {
    id: 'essence',
    name: 'Essence',
    icon: '✨',
    rarity: 'uncommon',
    description: 'Magical energy from defeated foes. Used for enchanting.',
    stackSize: 500,
  },

  crystal: {
    id: 'crystal',
    name: 'Crystal',
    icon: '💎',
    rarity: 'uncommon',
    description: 'Glowing crystal from treasure chests. Used for rare crafting.',
    stackSize: 500,
  },

  rune: {
    id: 'rune',
    name: 'Rune',
    icon: '🔮',
    rarity: 'uncommon',
    description: 'Ancient rune with mysterious power. Used for equipment upgrades.',
    stackSize: 500,
  },

  orcTusk: {
    id: 'orcTusk',
    name: 'Orc Tusk',
    icon: '🦷',
    rarity: 'uncommon',
    description: 'Massive tusk from an orc warrior. Used for crafting.',
    stackSize: 500,
  },

  darkSteel: {
    id: 'darkSteel',
    name: 'Dark Steel',
    icon: '⚫',
    rarity: 'uncommon',
    description: 'Corrupted metal from dark knights. Strong but cursed.',
    stackSize: 500,
  },

  ectoplasm: {
    id: 'ectoplasm',
    name: 'Ectoplasm',
    icon: '👻',
    rarity: 'uncommon',
    description: 'Ghostly residue from wraiths. Used for ethereal items.',
    stackSize: 500,
  },

  necroticEssence: {
    id: 'necroticEssence',
    name: 'Necrotic Essence',
    icon: '💀',
    rarity: 'uncommon',
    description: 'Essence of undeath. Used for cursed enchantments.',
    stackSize: 500,
  },

  // Rare materials
  soulShard: {
    id: 'soulShard',
    name: 'Soul Shard',
    icon: '💠',
    rarity: 'rare',
    description: 'Fragment of a powerful soul. Used for epic equipment.',
    stackSize: 250,
  },

  dragonScale: {
    id: 'dragonScale',
    name: 'Dragon Scale',
    icon: '🐉',
    rarity: 'rare',
    description: 'Scale from a legendary beast. Used for legendary armor.',
    stackSize: 250,
  },

  voidFragment: {
    id: 'voidFragment',
    name: 'Void Fragment',
    icon: '🌑',
    rarity: 'rare',
    description: 'Piece of the void itself. Used for powerful enchantments.',
    stackSize: 250,
  },

  demonHorn: {
    id: 'demonHorn',
    name: 'Demon Horn',
    icon: '👹',
    rarity: 'rare',
    description: 'Twisted horn from a demon. Radiates dark energy.',
    stackSize: 250,
  },

  sulfur: {
    id: 'sulfur',
    name: 'Sulfur',
    icon: '🌋',
    rarity: 'rare',
    description: 'Infernal brimstone. Used for fire-based enchantments.',
    stackSize: 250,
  },

  vampireFang: {
    id: 'vampireFang',
    name: 'Vampire Fang',
    icon: '🧛',
    rarity: 'rare',
    description: 'Fang from a vampire. Drains life force.',
    stackSize: 250,
  },

  bloodVial: {
    id: 'bloodVial',
    name: 'Blood Vial',
    icon: '🩸',
    rarity: 'rare',
    description: 'Vial of cursed blood. Used for dark rituals.',
    stackSize: 250,
  },

  ogreHeart: {
    id: 'ogreHeart',
    name: 'Ogre Heart',
    icon: '❤️',
    rarity: 'rare',
    description: 'Massive heart from an ogre chieftain. Still beating.',
    stackSize: 100,
  },

  thickHide: {
    id: 'thickHide',
    name: 'Thick Hide',
    icon: '🦏',
    rarity: 'rare',
    description: 'Incredibly tough hide from a boss monster.',
    stackSize: 100,
  },

  lichPhylactery: {
    id: 'lichPhylactery',
    name: 'Lich Phylactery',
    icon: '📿',
    rarity: 'rare',
    description: 'Container of a lich\'s soul. Immensely powerful.',
    stackSize: 50,
  },

  dragonHeart: {
    id: 'dragonHeart',
    name: 'Dragon Heart',
    icon: '🔥',
    rarity: 'rare',
    description: 'Still-burning heart of an ancient dragon.',
    stackSize: 50,
  },

  ancientBone: {
    id: 'ancientBone',
    name: 'Ancient Bone',
    icon: '🦴',
    rarity: 'rare',
    description: 'Fossilized bone from primordial times. Incredibly durable.',
    stackSize: 100,
  },

  // Epic materials
  divineEssence: {
    id: 'divineEssence',
    name: 'Divine Essence',
    icon: '⭐',
    rarity: 'epic',
    description: 'Pure divine energy. Used for godly equipment.',
    stackSize: 100,
  },

  eldritchHeart: {
    id: 'eldritchHeart',
    name: 'Eldritch Heart',
    icon: '❤️‍🔥',
    rarity: 'epic',
    description: 'Heart of an ancient horror. Used for cursed items.',
    stackSize: 100,
  },

  voidEssence: {
    id: 'voidEssence',
    name: 'Void Essence',
    icon: '🌌',
    rarity: 'epic',
    description: 'Pure essence of the void. Reality bends around it.',
    stackSize: 100,
  },

  cosmicFragment: {
    id: 'cosmicFragment',
    name: 'Cosmic Fragment',
    icon: '💫',
    rarity: 'epic',
    description: 'Fragment from the cosmos itself. Contains star matter.',
    stackSize: 100,
  },

  starMetal: {
    id: 'starMetal',
    name: 'Star Metal',
    icon: '⭐',
    rarity: 'epic',
    description: 'Metal forged in the heart of a dying star.',
    stackSize: 100,
  },

  // Legendary materials
  cosmicOre: {
    id: 'cosmicOre',
    name: 'Cosmic Ore',
    icon: '🌌',
    rarity: 'legendary',
    description: 'Ore from beyond the stars. The rarest material.',
    stackSize: 50,
  },

  eterniumIngot: {
    id: 'eterniumIngot',
    name: 'Eternium Ingot',
    icon: '🔱',
    rarity: 'legendary',
    description: 'Timeless metal that never dulls. Ultimate crafting material.',
    stackSize: 50,
  },
};

/**
 * Dungeon resource drop tables by rarity
 */
export const DUNGEON_DROP_TABLES = {
  // Common enemy drops (normal combat tiles)
  common: [
    { resource: 'bone', weight: 30, amount: [1, 3] },
    { resource: 'leather', weight: 25, amount: [1, 2] },
    { resource: 'cloth', weight: 25, amount: [1, 2] },
    { resource: 'ironOre', weight: 20, amount: [1, 2] },
  ],

  // Uncommon drops (treasure chests, elite enemies)
  uncommon: [
    { resource: 'essence', weight: 30, amount: [1, 2] },
    { resource: 'crystal', weight: 25, amount: [1, 2] },
    { resource: 'rune', weight: 25, amount: [1, 1] },
    { resource: 'bone', weight: 10, amount: [3, 5] },
    { resource: 'leather', weight: 10, amount: [2, 4] },
  ],

  // Rare drops (boss chests, rare enemies)
  rare: [
    { resource: 'soulShard', weight: 30, amount: [1, 1] },
    { resource: 'dragonScale', weight: 25, amount: [1, 1] },
    { resource: 'voidFragment', weight: 20, amount: [1, 1] },
    { resource: 'essence', weight: 15, amount: [2, 4] },
    { resource: 'crystal', weight: 10, amount: [2, 3] },
  ],

  // Epic drops (floor boss)
  epic: [
    { resource: 'divineEssence', weight: 40, amount: [1, 2] },
    { resource: 'eldritchHeart', weight: 30, amount: [1, 1] },
    { resource: 'soulShard', weight: 20, amount: [2, 3] },
    { resource: 'dragonScale', weight: 10, amount: [1, 2] },
  ],

  // Legendary drops (final boss, special events)
  legendary: [
    { resource: 'cosmicOre', weight: 45, amount: [1, 1] },
    { resource: 'eterniumIngot', weight: 35, amount: [1, 1] },
    { resource: 'divineEssence', weight: 20, amount: [2, 3] },
  ],
};

/**
 * Roll for dungeon resource drops
 * @param {string} dropTable - 'common', 'uncommon', 'rare', 'epic', 'legendary'
 * @param {number} seed - Random seed
 * @returns {Array} Array of {resourceId, amount} objects
 */
export function rollDungeonLoot(dropTable, seed = Date.now()) {
  const table = DUNGEON_DROP_TABLES[dropTable];
  if (!table) return [];

  // Simple seeded RNG
  let state = seed;
  const rng = () => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };

  const totalWeight = table.reduce((sum, entry) => sum + entry.weight, 0);
  const roll = rng() * totalWeight;

  let cumulative = 0;
  for (const entry of table) {
    cumulative += entry.weight;
    if (roll < cumulative) {
      const [min, max] = entry.amount;
      const amount = Math.floor(min + rng() * (max - min + 1));
      return [{ resourceId: entry.resource, amount }];
    }
  }

  return [];
}

/**
 * Get resource data by ID
 */
export function getDungeonResource(resourceId) {
  return DUNGEON_RESOURCES[resourceId] || null;
}

/**
 * Get all dungeon resources as array
 */
export function getAllDungeonResources() {
  return Object.values(DUNGEON_RESOURCES);
}

/**
 * Get dungeon resources by rarity
 */
export function getDungeonResourcesByRarity(rarity) {
  return Object.values(DUNGEON_RESOURCES).filter(r => r.rarity === rarity);
}
